import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime.js";
import updateLocale from "dayjs/plugin/updateLocale.js";
import minMax from "dayjs/plugin/minMax.js";
import isToday from "dayjs/plugin/isToday.js";
import isYesterday from "dayjs/plugin/isYesterday.js";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import buddhistEra from "dayjs/plugin/buddhistEra.js";
import th from "dayjs/locale/th";

const timeZone = "Asia/Bangkok";
dayjs.locale(th);
dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(minMax);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(advancedFormat);
dayjs.extend(buddhistEra);

const DEFAULT_FORMAT = "DD/MM/YYYY";
const BB_FORMAT = "D MMMM BBBB"; // Example 1 มกราคม 2567
const BB_SHORT_MONTH = "D MMM BBBB"; // Example: 1 ม.ค. 2567
const BB_SHORT_YEAR = "D MMMM BB"; // Example 1 มกราคม 67

const monthNames: string[] = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const formatDate = (
  date: Date | string | null | undefined,
  format: string = DEFAULT_FORMAT
) => {
  if (!date) return null;
  return dayjs(date).tz(timeZone).format(format);
};

export const formateDate = (
  dateValue: Date | string,
  format: string = "dd/MM/yyyy"
): string => {
  const initDate = new Date(dateValue);
  const date = initDate.getDate();
  const month = initDate.getMonth();
  const fullYear = initDate.getFullYear();
  switch (format) {
    case "dd mm, yyyy":
      return `${date} ${monthNames[month]}, ${fullYear}`;
    case "dd/MM/yyyy":
      return `${date}/${formatNumberWithLeadingZeros(
        month + 1,
        2
      )}/${fullYear}`;
    default:
      return dayjs(dateValue).format(format);
  }
};

export const getFullYear = (date: Date | string) => {
  return new Date(date).getFullYear();
};
export const getMonth = (date: Date | string) => {
  return new Date(date).getMonth();
};
export const getDate = (date: Date | string) => {
  return new Date(date).getDate();
};

export const getDateOnly = (date: Date | string) => {
  if (!date) return "";
  // Format the date as YYYY-MM-DD
  const year = getFullYear(date);
  const month = getMonth(date);
  const day = getDate(date);
  return new Date(year, month, day);
};

export const getTimeOnly = (dateStr: Date | string) => {
  // Parse the date string to a dayjs object
  const date = dayjs(dateStr);

  // Format the date to get only the time part
  const timeOnly = date.format("HH:mm:ss");

  return timeOnly;
};

export const formatNumberWithLeadingZeros = (
  number: number,
  length: number
) => {
  return number.toString().padStart(length, "0");
};

export const addTimeToDate = (
  dateStr: Date | string,
  timeStr: Date | string
) => {
  // Parse the date string to a dayjs object
  let date = dayjs(dateStr);

  let timeOnlystr = getTimeOnly(timeStr);

  // Split the time string into hours, minutes, and seconds
  let [hours, minutes, seconds] = timeOnlystr.split(":").map(Number);

  // Set the time on the date
  date = date.hour(hours).minute(minutes).second(seconds);

  return date;
};

export const toJSONWithTimezone = (dateStr: string, tz: string) => {
  const date = dayjs(dateStr).tz(tz);
  return {
    date: date.format(), // Default ISO 8601 string with timezone
    timezone: date.tz(), // Explicit timezone
    unix: date.unix(), // Unix timestamp
    utc: date.utc().format(), // UTC ISO 8601 string
  };
};

export const getAfterDateFromDay = (options: {
  date?: Date | string;
  endDay: number;
}) => {
  const { date, endDay } = options;
  const day = (date && new Date(date)) || new Date();
  const endDate = day;
  endDate.setDate(day.getDate() + endDay);
  return endDate;
};

export const formatDateBE = (date: Date | string | null | undefined) => {
  if (!date) return null;
  const _date = new Date(date);
  const _dateBE = new Date(
    _date.getFullYear(),
    _date.getMonth(),
    _date.getDate()
  );
  return dayjs(_dateBE).tz(timeZone).format(BB_FORMAT);
};

export const formatDateShortBE = (date: Date | string | null | undefined) => {
  if (!date) return null;
  const _date = new Date(date);
  const _dateBE = new Date(
    _date.getFullYear(),
    _date.getMonth(),
    _date.getDate()
  );
  return dayjs(_dateBE).tz(timeZone).format(BB_SHORT_MONTH);
};

export const formatDateTimeBE = (date: Date | string | null | undefined) => {
  if (!date) return null;
  const _date = new Date(date);
  const _dateBE = new Date(
    _date.getFullYear(),
    _date.getMonth(),
    _date.getDate(),
    _date.getHours(),
    _date.getMinutes()
  );
  return dayjs(_dateBE).tz(timeZone).format("D MMM BBBB, HH:mm");
};

export const updateDateOnly = (originalDate: Date, newDate: Date): Date => {
  // Extract the time portion of the original date
  const hours = originalDate.getHours();
  const minutes = originalDate.getMinutes();
  const seconds = originalDate.getSeconds();
  const milliseconds = originalDate.getMilliseconds();

  // Create a new date with the new date but with the original time
  const updatedDate = new Date(newDate);
  updatedDate.setHours(hours, minutes, seconds, milliseconds);

  return updatedDate;
};

export const updateStrDateOnly = (time: string, newDate: Date): Date => {
  const [hoursStr, minutesStr] = time.split(":");
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Create a new date with the new date but with the original time
  const updatedDate = new Date(newDate);
  updatedDate.setHours(hours, minutes, 0, 0);

  return updatedDate;
};

export const formateTimeBetween = (
  startDate: Date | string,
  endDate: Date | string
) => {
  return (
    dayjs(startDate).format("h:mm A") + " - " + dayjs(endDate).format("h:mm A")
  );
};

export const convertDateTimeToTimeValue = (dateTime: string): number => {
  const date = new Date(dateTime);

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const timeValue = parseInt(`${hours}${minutes}`, 10);

  return timeValue;
};

export const flightDateTimeStrToAppFormat = (dateTime: string): string => {
  // Create a Date object
  const date = new Date(dateTime);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    throw new Error("Invalid date format");
  }

  // Extract date components
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  // Return the formatted date
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};
